// Markdown <-> ProseMirror, headless (no DOM, no live `Editor`), on
// `@tiptap/markdown`'s MarkdownManager. The manager works on ProseMirror JSON;
// `schema.nodeFromJSON` at the boundary makes this module speak real nodes.

import { MarkdownManager } from "@tiptap/markdown";
import { DOMSerializer, type Node } from "@tiptap/pm/model";
import { Marked, type marked } from "marked";
import { withActiveLexer } from "./active-lexer";
import { cjkAutolink } from "./cjk-autolink";
import { cjkEmphasis } from "./cjk-emphasis";
import { collectFootnoteDefs } from "./footnotes";
import { splitListBlocks } from "./list-blocks";
import {
  type EditorProfile,
  getEditorSchema,
  getProfileExtensions,
} from "./schema";
import { mergeTableOptions } from "./table";

export type { EditorProfile } from "./schema";

/**
 * One manager per profile, made once. The manager registers its tokenizers
 * with `marked.use()`; making one per call would register them again and again
 * on the same marked instance. It keeps no state between parses.
 *
 * Each manager has its own marked instance (not the global one): the CJK
 * emphasis rule changes how `**` is read, for exactly these documents, and two
 * managers on one instance would register their tokenizers twice. The option
 * is typed as the global (`typeof marked`), but the manager only touches
 * `defaults` / `Lexer` / `lexer` / `use` / `setOptions`, all on `Marked`.
 */
const managers = new Map<string, MarkdownManager>();

/** How the markdown is read. */
export interface ParseOptions {
  /**
   * A single newline inside a paragraph is a line break (marked's `breaks`),
   * not a space: text typed into a plain box, where Enter means a new line.
   */
  lineBreaks?: boolean;
}

function managerOf(
  profile: EditorProfile,
  { lineBreaks = false }: ParseOptions = {},
): MarkdownManager {
  const key = `${profile}:${lineBreaks}`;
  let manager = managers.get(key);
  if (manager) return manager;
  const markedInstance = new Marked(cjkEmphasis, cjkAutolink);
  // The manager makes a `markedInstance.Lexer` for every lexing; that lexer is
  // made current while it runs (active-lexer.ts), so task items resolve their
  // text after the reference definitions further down.
  class TrameriaLexer extends markedInstance.Lexer {
    override lex(src: string) {
      return withActiveLexer(this, () => super.lex(src));
    }
  }
  markedInstance.Lexer = TrameriaLexer as typeof markedInstance.Lexer;
  manager = new MarkdownManager({
    extensions: getProfileExtensions(profile),
    marked: markedInstance as unknown as typeof marked,
    markedOptions: lineBreaks ? { breaks: true } : undefined,
  });
  managers.set(key, manager);
  return manager;
}

/**
 * Parses markdown into a document of the profile's schema
 * (`getEditorSchema(profile)`, the same instance every time).
 *
 * After the manager: `mergeTableOptions` before `nodeFromJSON` (its marker is
 * not a schema node); `collectFootnoteDefs` gathers definitions at the end;
 * `splitListBlocks` makes one block per top-level list item.
 */
export function parseMarkdown(
  md: string,
  profile: EditorProfile = "document",
  options?: ParseOptions,
): Node {
  const json = managerOf(profile, options).parse(md);
  return getEditorSchema(profile).nodeFromJSON(
    splitListBlocks(collectFootnoteDefs(mergeTableOptions(json))),
  );
}

/** The canonical markdown of a document (of either profile). */
export function serializeMarkdown(doc: Node): string {
  const profile: EditorProfile =
    doc.type.schema === getEditorSchema("note") ? "note" : "document";
  return managerOf(profile).serialize(doc.toJSON());
}

/** `serialize(parse(md))`: the canonical form of `md`. */
export function normalizeMarkdown(
  md: string,
  profile: EditorProfile = "document",
): string {
  return serializeMarkdown(parseMarkdown(md, profile));
}

/** The DOM a static render needs: a browser `Document` or a test shim. */
export interface DocumentLike {
  createElement(tag: string): {
    appendChild(node: never): unknown;
    innerHTML: string;
  };
}

/**
 * Markdown as static HTML through the editor's own schema (`parseMarkdown` +
 * ProseMirror's `DOMSerializer`), so it has the same structure as the live
 * editor: the same paragraph and line break split, tables as `<table>`,
 * diagrams as `<pre data-trameria-fence>`, references as `<span data-trameria-link>`.
 *
 * Why: the screen shows a static rendering until the Y.Doc has synced, then
 * swaps in the editor. A different markdown pipeline for that made the page
 * visibly re-layout at the swap; the same parser removes the difference.
 *
 * Safety: the HTML comes from `DOMSerializer` over a schema-constrained
 * document, never from concatenating the input. Raw HTML is kept as the text
 * of an opaque block and comes out escaped. Needs a `Document`.
 */
export function renderMarkdownToHtml(
  md: string,
  doc: DocumentLike = (globalThis as { document?: DocumentLike }).document ??
    missingDocument(),
  profile: EditorProfile = "document",
  options?: ParseOptions,
): string {
  const node = parseMarkdown(md, profile, options);
  // `DOMSerializer` wants lib.dom's `Document`; this package has no DOM types
  // and only `createElement` is used, so a structural type is accepted.
  const fragment = DOMSerializer.fromSchema(
    getEditorSchema(profile),
  ).serializeFragment(node.content, { document: doc as never });
  const container = doc.createElement("div");
  container.appendChild(fragment as never);
  return container.innerHTML;
}

function missingDocument(): never {
  throw new Error("renderMarkdownToHtml needs a document");
}
